'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Tritan Plastic: What It Actually Is, Whether It's Safe in Hot Water, and What Really Sheds"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="14 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Tritan plastic explained: Eastman's BPA-free copolyester made from DMT, CHDM and TMCD, its heat limits, and where microplastic shedding actually comes from"
    >
      <BlogLastUpdated iso="2026-08-14" formatted="August 14, 2026" />

      <BlogTLDR>
        <strong>Tritan is a plastic, but it is not a microplastic — and the
        particles measured in reusable bottles mostly do not come from the
        Tritan.</strong>{' '}
        It is Eastman&rsquo;s copolyester, built from three monomers: DMT, CHDM
        and TMCD. That third one is the whole story — TMCD pushes the glass
        transition temperature to roughly <strong>120&nbsp;&deg;C</strong>,
        against about 80&nbsp;&deg;C for ordinary copolyesters, which is why
        Tritan survives dishwashers that would haze other clear plastics.{' '}
        <strong>There is no bisphenol anywhere in that monomer list</strong>, so
        &ldquo;BPA-free&rdquo; here is structural rather than a substitution —
        it did not swap BPA for BPS the way many &ldquo;BPA-free&rdquo; plastics
        did. On safety: a decade-old claim that Tritan showed estrogenic
        activity went to court, and{' '}
        <strong>a jury found those claims were false statements under the
        Lanham Act, upheld on appeal in December 2014.</strong> And the part
        almost nobody mentions: when researchers actually counted particles from
        reusable bottles, the source was{' '}
        <strong>the screw cap</strong> — 131 &plusmn; 25 particles per litre
        after a single opening, rising to 242 &plusmn; 64 after eleven. You are
        worrying about the bottle. The measurement points at the lid.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Tritan is a bulk material, not a microplastic.</strong> A microplastic is a particle under 5&nbsp;mm; Tritan is the resin a bottle is moulded from. But like any plastic it can shed particles.</>,
          <><strong>Three monomers: DMT, CHDM and TMCD.</strong> CHDM is over half the formulation; TMCD is the rigid one that raises the glass transition temperature to around <strong>120&nbsp;&deg;C</strong>.</>,
          <><strong>BPA-free by structure, not by substitution.</strong> No bisphenol appears in the monomer set at all, which is a meaningfully stronger claim than a plastic that replaced BPA with BPS.</>,
          <><strong>The estrogenic-activity dispute ended in court, not in a stalemate.</strong> A jury found the claims were false under the Lanham Act; the Fifth Circuit affirmed in December 2014. That is a false-advertising verdict, not a declaration of safety — both facts matter.</>,
          <><strong>FDA-cleared for food contact to 100&nbsp;&deg;C</strong> for Tritan Renew, and some grades meet infant-bottle sterilisation by boiling or steam. Hot water is not the problem people assume.</>,
          <><strong>The screw cap is the measured shedding source.</strong> 131 &plusmn; 25 particles/litre after one opening, 242 &plusmn; 64 after eleven — abrasion at the threads, and those threads are often polypropylene, not Tritan.</>,
          <><strong>Polypropylene is far worse under heat</strong> — release climbing from 2&times;10&#8309; to 3&times;10&#8311; particles/litre as temperature goes from 25 to 100&nbsp;&deg;C.</>,
        ]}
      />

      <KeyStats
        heading="Tritan by the numbers"
        stats={[
          { value: '120 °C', label: 'glass transition temperature', context: 'against roughly 80 °C for conventional copolyesters — the TMCD monomer is what buys this', source: 'Tritan copolyester composition and properties', sourceUrl: 'https://en.wikipedia.org/wiki/Tritan_copolyester' },
          { value: '3', label: 'monomers, none of them a bisphenol', context: 'DMT (rigidity), CHDM (clarity and flexibility, >50%), TMCD (heat and impact resistance)', source: 'Osimitz et al., Food and Chemical Toxicology, 2012', sourceUrl: 'https://www.sciencedirect.com/science/article/abs/pii/S0278691512000865' },
          { value: '100 °C', label: 'FDA-cleared food contact temperature', context: 'for Tritan Renew — boiling point; some grades also meet infant sterilisation requirements', source: 'Eastman Tritan FAQs', sourceUrl: 'https://www.eastman.com/en/products/brands/tritan/about/faqs' },
          { value: '131 → 242', label: 'particles/litre from the screw cap', context: '± 25 after one opening, ± 64 after eleven openings and closings — cap abrasion, not bottle wall', source: 'Winkler et al., ACS ES&T Water, 2020', sourceUrl: 'https://pubs.acs.org/doi/10.1021/acsestwater.0c00238' },
          { value: '3×10⁷', label: 'particles/litre from polypropylene at 100 °C', context: 'up from 2×10⁵ at 25 °C — the temperature effect in a different plastic entirely', source: 'Microplastics from polypropylene bottles, 2025', sourceUrl: 'https://www.sciencedirect.com/science/article/pii/S2215153225000649' },
        ]}
      />

      <h2>What Tritan actually is</h2>
      <p>
        Most explanations stop at &ldquo;a BPA-free plastic&rdquo;, which tells
        you nothing. Here is the real answer.
      </p>
      <p>
        Tritan is a <strong>copolyester</strong> — a polyester made from more
        than one alcohol component — produced by Eastman through melt
        copolymerisation of three monomers:
      </p>
      <ul>
        <li>
          <strong>DMT (dimethyl terephthalate)</strong> — supplies the rigid
          aromatic ring that gives strength and heat resistance. This is the
          same backbone chemistry found in PET, the plastic in a disposable
          water bottle.
        </li>
        <li>
          <strong>CHDM (1,4-cyclohexanedimethanol)</strong> — more than half the
          formulation. Its ring structure adds chain flexibility and chemical
          stability, and it is a large part of why Tritan is glass-clear rather
          than hazy.
        </li>
        <li>
          <strong>TMCD (2,2,4,4-tetramethyl-1,3-cyclobutanediol)</strong> — the
          monomer that makes Tritan <em>Tritan</em>. Its four methyl groups
          create a bulky, sterically hindered structure that stiffens the
          polymer chain and pushes the{' '}
          <strong>glass transition temperature to about 120&nbsp;&deg;C</strong>,
          where conventional copolyesters sit nearer 80.
        </li>
      </ul>
      <p>
        The resulting polymer is <strong>amorphous</strong> — it crystallises so
        slowly that in practice it does not, staying in a glassy state at room
        temperature. That is what delivers the combination that made it the
        default drinkware plastic: optical clarity like glass, impact resistance
        that survives being dropped on tile, and enough heat tolerance to go
        through a dishwasher repeatedly without clouding or stress-cracking.
      </p>

      <h2>Why &ldquo;BPA-free&rdquo; means something here</h2>
      <p>
        &ldquo;BPA-free&rdquo; is one of the emptiest phrases in consumer
        products, because a great many plastics earned it by replacing
        bisphenol&nbsp;A with bisphenol&nbsp;S or bisphenol&nbsp;F — structural
        cousins with broadly similar endocrine concerns. The label changed; the
        chemistry barely did.
      </p>
      <p>
        Tritan is a genuine exception, and the reason is in the monomer list
        above.{' '}
        <strong>There is no bisphenol in it at all — not BPA, not BPS, not
        BPF.</strong> Polycarbonate, the plastic Tritan largely displaced in
        drinkware, is built <em>from</em> BPA: the bisphenol is a structural
        component of the polymer, not an additive. Tritan is a different class
        of polymer that never had one to remove.
      </p>
      <p>
        That is a real distinction and worth granting plainly. It is also not
        the same as &ldquo;inert&rdquo; or &ldquo;sheds nothing&rdquo;, which is
        where most coverage overreaches. See{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">is BPA-free plastic safe</Link>{' '}
        for how the substitution problem plays out across the wider market.
      </p>

      <h2>The estrogenic-activity fight, and how it actually ended</h2>
      <p>
        This is the part of the Tritan story that gets left as &ldquo;disputed,
        unresolved&rdquo; almost everywhere, including in an earlier version of
        this article. The record is more specific than that.
      </p>
      <p>
        Two companies, <strong>CertiChem</strong> and{' '}
        <strong>PlastiPure</strong>, published and marketed findings that Tritan
        products showed <strong>estrogenic activity</strong> under certain test
        conditions. Eastman sued in January 2012. The case went to a jury, and{' '}
        <strong>the jury found that CertiChem and PlastiPure had made false
        statements of fact about Tritan in violation of the Lanham Act</strong>,
        the federal false-advertising statute. The district court issued an
        injunction, and in <strong>December 2014 the Fifth Circuit
        affirmed</strong>, holding that the Lanham Act reaches false commercial
        speech even when that speech takes the form of scientific claims.
      </p>
      <p>
        Two things are true at once here, and skipping either produces a
        misleading article:
      </p>
      <ul>
        <li>
          <strong>The claims were adjudicated false in a commercial
          context</strong>, and the companies making them had a direct financial
          interest — they sold estrogenic-activity testing services and
          promoted competing plastics. That is a material conflict, and it
          belongs in any honest summary.
        </li>
        <li>
          <strong>A false-advertising verdict is not a scientific finding of
          safety.</strong> A jury decided that specific commercial statements
          were false and misleading. It did not, and could not, certify that
          Tritan is free of biological activity for all time. And the principal
          study finding <em>no</em> estrogenicity in the three monomers —
          Osimitz et al., 2012 — was <strong>funded by Eastman</strong>, which
          should be stated rather than buried.
        </li>
      </ul>
      <p>
        The fair reading in 2026: the specific allegation against Tritan was
        tested in court and failed, the evidence for it was weak and
        commercially motivated, and the countervailing evidence is
        industry-funded. That is a much stronger position than most plastics
        can claim, and it is still not the same as independent confirmation.
      </p>

      <InlineGuessReveal
        source="tritan_cap_vs_bottle"
        question="Researchers counted microplastics from reusable plastic bottles. Where did the particles mostly come from?"
        options={[
          { label: 'The bottle wall, from flexing' },
          { label: 'The screw cap threads', correct: true },
          { label: 'The printed logo' },
          { label: 'The silicone seal' },
        ]}
        answer="The screw cap — 131 particles/litre after one opening"
        answerNote={
          <>
            Raman microspectroscopy of reusable bottles found 131 &plusmn; 25
            particles per litre after a single opening, rising to 242 &plusmn;
            64 after eleven openings and closings. The mechanism is simple
            abrasion: two threaded surfaces grinding past each other every time
            you open the bottle. On many bottles the body is Tritan and the cap
            is polypropylene — so the material people scrutinise is not the one
            doing the shedding.
          </>
        }
        ctaText="The lid is a different plastic from the bottle more often than not."
        ctaLabel="Scan your bottle"
      />

      <RiskQuizInlineNudge source="blog_inline_tritan" />

      <h2>Hot water, dishwashers, and the limits that actually apply</h2>
      <p>
        This is where Tritan performs better than its reputation suggests, and
        where the generic advice — <em>never put hot liquid in plastic</em> —
        is too blunt to be useful.
      </p>
      <p>
        <strong>Eastman&rsquo;s Tritan Renew is FDA-cleared for food contact at
        temperatures up to and including 100&nbsp;&deg;C</strong>, which is
        boiling. Certain grades are specified for infant-care applications and
        meet sterilisation requirements by boiling water or microwave steam.
        Dishwasher performance is a design goal rather than a tolerance: Tritan
        grades are engineered for repeated residential and commercial cycles
        without the hazing and stress-cracking that destroy other clear
        plastics.
      </p>
      <p>
        The honest qualifications:
      </p>
      <ul>
        <li>
          <strong>Cleared for 100&nbsp;&deg;C is not the same as indefinite
          at 100&nbsp;&deg;C.</strong> Sustained high heat, repeated steam
          sterilisation and prolonged boiling will still age the polymer, and
          an aged, micro-cracked surface is a shedding surface.
        </li>
        <li>
          <strong>Heat drives shedding in every plastic, and the effect is
          enormous.</strong> In polypropylene bottles, particle release rose
          from about 2&times;10&#8309; to 3&times;10&#8311; particles per litre
          as temperature went from 25 to 100&nbsp;&deg;C — roughly two orders of
          magnitude. Tritan is more heat-stable than PP, but the direction of
          travel is identical.
        </li>
        <li>
          <strong>Mechanical stress compounds it.</strong> A 2022 study of
          reusable Tritan and polypropylene bottles found that squeezing,
          temperature cycling and dishwasher exposure each increased particle
          release. Dishwashers combine all three.
        </li>
      </ul>
      <p>
        Practical version: a Tritan bottle with cold water, hand-washed, is a
        low-shedding object. The same bottle after four years of daily
        dishwasher cycles is not the same object. For what heat does inside a
        parked car, see{' '}
        <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-forest underline">a water bottle left in a hot car</Link>.
      </p>

      <h2>The finding that reframes the question</h2>
      <p>
        When researchers applied Raman microspectroscopy to reusable plastic
        bottles, they were not measuring the wall of the bottle. They measured{' '}
        <strong>the screw cap system</strong>, and found{' '}
        <strong>131 &plusmn; 25 microplastic particles per litre after one
        opening, rising to 242 &plusmn; 64 after eleven openings and
        closings.</strong>
      </p>
      <p>
        The mechanism needs no exotic chemistry. Two threaded plastic surfaces
        grind against each other under torque, every single time you open the
        bottle. That is a machining operation performed a few times a day, for
        years, directly above the water.
      </p>
      <p>
        And on a great many bottles — including popular models where the body is
        marketed on being Tritan —{' '}
        <strong>the cap and its threads are polypropylene, a softer and
        markedly higher-shedding plastic.</strong> So the component under
        consumer scrutiny is the clear body, and the component generating the
        measured particles is the opaque lid nobody looks at. It is the same
        inversion we keep finding: the part that looks like the problem usually
        is not.
      </p>

      <h2>Tritan against the alternatives</h2>
      <BlogComparisonTable
        caption="Bottle materials compared on what actually drives microplastic exposure (checked August 2026)"
        columns={[
          { key: 'm', label: 'Material' },
          { key: 'what', label: 'What it is' },
          { key: 'heat', label: 'Heat behaviour' },
          { key: 'sheds', label: 'Shedding' },
          { key: 'v', label: 'Verdict' },
        ]}
        rows={[
          { m: 'Glass', what: 'Inert silicate', heat: 'Unaffected', sheds: 'None from the vessel', v: 'The clean answer; weight and breakage are the trade' },
          { m: 'Stainless steel', what: 'Inert alloy', heat: 'Unaffected', sheds: 'None from the body', v: 'Excellent — but check what the lid is made of' },
          { m: 'Tritan copolyester', what: 'DMT + CHDM + TMCD, no bisphenol', heat: 'Tg ≈ 120 °C; FDA-cleared to 100 °C', sheds: 'Low from the body; the cap dominates', v: 'The best-performing common drinkware plastic' },
          { m: 'Polypropylene (#5)', what: 'Polyolefin, very common in caps', heat: 'Softens far lower', sheds: 'High, and rises sharply with heat', v: 'Usually the real source in a "Tritan" bottle' },
          { m: 'PET (#1)', what: 'Single-use bottle plastic', heat: 'Poor above ~60 °C', sheds: 'High when reused, heated or aged', v: 'Not built for repeat use' },
          { m: 'Polycarbonate (legacy)', what: 'Polymerised from BPA itself', heat: 'Good, but hydrolyses', sheds: 'Yes, and releases BPA', v: 'Avoid — this is what Tritan replaced' },
        ]}
      />

      <BlogAppCTA
        headline="Find out what your bottle and its lid are actually made of"
        sub="The body and the cap are frequently different plastics, and the cap is usually the one that matters. Scan any bottle with MicroPlastics to identify both and get a 0–100 score."
      />

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Judge the cap, not the bottle.</strong> This is the single
          highest-value change. A Tritan body with a polypropylene screw cap is
          a Tritan bottle with a polypropylene shedding problem.
        </li>
        <li>
          <strong>Prefer a lid that lifts or flips over one that
          threads.</strong> Every screw action is an abrasion cycle. Flip-tops
          and straw lids do far less grinding.
        </li>
        <li>
          <strong>Hand-wash if the bottle is old.</strong> Heat plus mechanical
          stress plus time is the shedding recipe, and dishwashers deliver all
          three at once. A new Tritan bottle handles it; a four-year-old
          scratched one is a different proposition.
        </li>
        <li>
          <strong>Retire scratched bottles.</strong> Visible scratching means
          the surface is already fragmenting. No plastic recovers from that,
          however good the resin.
        </li>
        <li>
          <strong>Do not read &ldquo;BPA-free&rdquo; as
          &ldquo;particle-free&rdquo;.</strong> Tritan earns its BPA-free claim
          honestly, and that claim says nothing whatsoever about shedding. They
          are unrelated properties.
        </li>
        <li>
          <strong>If the goal is zero, the answer is still{' '}
          <Link href="/blog/best-glass-water-bottles" className="text-forest underline">glass</Link> or{' '}
          <Link href="/blog/best-stainless-steel-water-bottles" className="text-forest underline">steel</Link>.</strong>{' '}
          Tritan is the best of the common drinkware plastics
          and it remains a plastic. Check the lid on your steel bottle too — it
          is very often plastic.
        </li>
      </ul>

      <p>
        For the concern-by-concern spec sheet — heat, abrasion, fatty food and
        acid, scored individually — see the{' '}
        <Link href="/materials/tritan" className="text-forest underline">Tritan material profile</Link>.
        For the full material ranking see{' '}
        <Link href="/blog/refillable-water-bottle-materials-compared" className="text-forest underline">bottle materials compared</Link>{' '}
        and{' '}
        <Link href="/blog/best-glass-water-bottles" className="text-forest underline">the best glass water bottles</Link>.
        For specific bottles built on Tritan, see{' '}
        <Link href="/blog/best-owala-water-bottles-microplastics" className="text-forest underline">Owala</Link>,{' '}
        <Link href="/blog/does-cirkul-have-microplastics" className="text-forest underline">Cirkul</Link>{' '}
        and{' '}
        <Link href="/blog/are-nalgene-bottles-safe-microplastics" className="text-forest underline">Nalgene</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'What is Tritan material?',
            answer: 'Tritan is a copolyester made by Eastman from three monomers: dimethyl terephthalate (DMT), 1,4-cyclohexanedimethanol (CHDM, more than half the formulation) and 2,2,4,4-tetramethyl-1,3-cyclobutanediol (TMCD). TMCD is the distinguishing component — its bulky methyl groups stiffen the polymer chain and raise the glass transition temperature to roughly 120 °C, against about 80 °C for conventional copolyesters. The polymer is amorphous, which gives it glass-like clarity along with impact and dishwasher resistance.',
          },
          {
            question: 'Is Tritan a microplastic?',
            answer: 'No. A microplastic is a plastic particle smaller than 5 millimetres, while Tritan is the bulk resin a bottle is moulded from. However, being a plastic, a Tritan product can shed microplastic particles through heat, abrasion and age. Notably, when researchers measured particle release from reusable bottles the dominant source was the screw cap rather than the bottle body — and caps are frequently polypropylene rather than Tritan.',
          },
          {
            question: 'Is Tritan plastic safe?',
            answer: 'Tritan contains no bisphenols of any kind — not BPA, BPS or BPF — because none appears in its monomer chemistry, which is a stronger position than plastics that merely substituted BPA for BPS. Claims that Tritan showed estrogenic activity were made by CertiChem and PlastiPure, litigated by Eastman, and found by a jury to be false statements under the Lanham Act, a ruling the Fifth Circuit upheld in December 2014. That is a false-advertising verdict rather than a scientific declaration of safety, and the main study finding no estrogenicity in the monomers was funded by Eastman. On the available record Tritan is among the better-characterised consumer plastics, while remaining a plastic.',
          },
          {
            question: 'Is Tritan plastic safe for hot water?',
            answer: 'Better than most plastics. Tritan Renew is FDA-cleared for food contact at temperatures up to and including 100 °C, and certain grades meet infant-bottle sterilisation requirements by boiling or microwave steam. Its glass transition temperature is around 120 °C. That said, heat accelerates microplastic shedding in every plastic — release from polypropylene bottles rose roughly two orders of magnitude between 25 °C and 100 °C — so repeated hot use will age any bottle. Cold use and hand-washing extend the life of the surface.',
          },
          {
            question: 'Is Tritan dishwasher safe?',
            answer: 'Yes, and this is a specific design objective rather than a tolerance. Tritan grades are engineered for repeated residential and commercial dishwasher cycles without the hazing and stress-cracking that degrade other clear plastics. The qualification is that a dishwasher applies heat, moisture and mechanical stress simultaneously, and all three drive particle release over time. For a new bottle this is a non-issue; for an old or scratched one, hand-washing is the better choice.',
          },
          {
            question: 'Does Tritan release microplastics?',
            answer: 'It can, like any plastic, but it is not usually the main source in a bottle. Raman microspectroscopy of reusable bottles found 131 ± 25 particles per litre after a single opening, rising to 242 ± 64 after eleven openings and closings, with the screw cap threads identified as the origin — abrasion between two threaded surfaces. On many bottles the body is Tritan while the cap is polypropylene, a softer and higher-shedding plastic. Squeezing, temperature cycling and dishwasher exposure all increase release.',
          },
          {
            question: 'Tritan vs glass: which is better?',
            answer: 'Glass is better if the only criterion is microplastics. It is inert, sheds no particles, is unaffected by heat and does not degrade with age or scratching. Tritan wins on weight, shatter resistance and safety around children, which is why it dominates sports and outdoor drinkware. Tritan is the best-performing of the common drinkware plastics; it is not equivalent to glass. If you do choose glass, check the lid, which is frequently plastic.',
          },
          {
            question: 'Is Tritan BPA-free, really?',
            answer: 'Yes, and unusually so. Many plastics claim BPA-free after substituting bisphenol S or F, which are structurally similar and carry related endocrine concerns. Tritan contains no bisphenol of any kind because none features in its three monomers. Polycarbonate, the plastic Tritan largely replaced in drinkware, is polymerised from BPA itself — the bisphenol is structural rather than an additive. Tritan is a different class of polymer that never contained one.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Osimitz TG, Eldridge ML, Sloter E, Welsh W, Ai N, Sayler GS, Menn F, Toole C', year: 2012, title: 'Lack of androgenicity and estrogenicity of the three monomers used in Eastman’s Tritan copolyesters (DMT, CHDM and TMCD) — study funded by Eastman Chemical Company', publisher: 'Food and Chemical Toxicology 50(6)', url: 'https://www.sciencedirect.com/science/article/abs/pii/S0278691512000865' },
          { authors: 'United States Court of Appeals, Fifth Circuit', year: 2014, title: 'Eastman Chemical Co. v. PlastiPure, Inc. and CertiChem, Inc., No. 13-51087 — jury finding of false statements under the Lanham Act affirmed, 22 December 2014', publisher: 'Justia / 5th Cir.', url: 'https://law.justia.com/cases/federal/appellate-courts/ca5/13-51087/13-51087-2014-12-22.html' },
          { authors: 'Winkler A, Santo N, Ortenzi MA, Bolzoni E, Bacchetta R, Tremolada P', year: 2020, title: 'A preliminary study of microplastic abrasion from the screw cap system of reusable plastic bottles by Raman microspectroscopy (131 ± 25 particles/L after one opening; 242 ± 64 after eleven)', publisher: 'ACS ES&T Water', url: 'https://pubs.acs.org/doi/10.1021/acsestwater.0c00238' },
          { authors: 'Eastman Chemical Company', year: 2026, title: 'Tritan and Tritan Renew — frequently asked questions, including FDA food-contact clearance to 100 °C and dishwasher durability', publisher: 'Eastman', url: 'https://www.eastman.com/en/products/brands/tritan/about/faqs' },
          { authors: 'Science of the Total Environment / ScienceDirect', year: 2025, title: 'Multiple characteristics of microplastics generated from polypropylene bottles — release rising from 2×10⁵ to 3×10⁷ particles/L between 25 °C and 100 °C', publisher: 'ScienceDirect', url: 'https://www.sciencedirect.com/science/article/pii/S2215153225000649' },
        ]}
      />
    </BlogPostLayout>
  )
}
